import {Inject, Injectable} from "@angular/core";
import {RegistrationService} from "../registration/registration.service";
import {AccountService} from "../account/account.service";
import {FormBuilder, Validators} from "@angular/forms";
import {OrderService} from "../order/order.service";
import {ExchangeService} from "../exchange/exchange.service";
import {ProductService} from "../product/product.service";
import {from, Observable, of, switchMap} from "rxjs";
import {CountryService} from "../country/country.service";
import {GoogleMapsLoader} from "../googlemaps/googlemapsloader.service";
import {CustomValidators} from "../validators/custom.validators";
import {AccountControllerService} from "@aparraga/ahoracrypto-api-sdk/sdk/api/accountController.service";
import {CartService} from "../cart/cart.service";
import {CurrencyService} from "../currency/currency.service";
import {CryptoService} from "../crypto/crypto.service";
import {AbstractFlowHelperService} from "./abstractflowhelper.service";
import {RecaptchaService} from "../recaptcha/recaptcha.service";
import {I18nServices} from "../i18n/i18n.service";

declare var google: any;

@Injectable({
  providedIn: 'root'
})
export class RegistrationFlowHelperService extends AbstractFlowHelperService {

  public registrationFields = ['email', 'phone', 'phonePrefix', 'firstName', 'lastName', 'country', 'street', 'apartment', 'postalCode', 'acceptTou'];

  public autocomplete: any;

  public temporalUserId: string;

  public constructor(protected registrationService: RegistrationService,
                     protected accountService: AccountService,
                     @Inject(FormBuilder) protected formBuilder: FormBuilder,
                     protected exchangeService: ExchangeService,
                     protected googleMapsLoader: GoogleMapsLoader,
                     protected i18nService: I18nServices,
                     protected customValidators: CustomValidators,
                     protected countryService: CountryService,
                     protected currencyService: CurrencyService,
                     protected recaptchaService: RecaptchaService,
                     protected cryptoService: CryptoService,
                     protected accountControllerService: AccountControllerService,
                     protected productService: ProductService,
                     protected cartService: CartService,
                     protected orderService: OrderService) {

    super();

    this.buildForm();

    this.countryService.onCountrySelected$.subscribe(country => {
      const streetElement = document.getElementById('street');
      if (!!streetElement) {
        this.loadGoogleMapsAutocomplete()
          .then(() => this.changeAutocompleteCountry(country))
          .catch(error => {
            console.error('Error loading Google Maps autocomplete', error);
          });
        }
    });

  }


  public buildForm() {

    let phonePrefix = this.registrationService.getPhonePrefix();
    if(phonePrefix == null && this.countryService.getSelectedCountry() != null) {
      phonePrefix = this.countryService.getSelectedCountry().phonePrefix;
    }

    this.form = this.formBuilder.group({
      email: [this.registrationService.getEmail(), [
        Validators.required,
        Validators.pattern(/^\s*[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\s*$/i)
      ]],
      phone: [this.registrationService.getPhone(), [
        Validators.required,
        this.customValidators.validatePhoneNumber()
      ]],
      phonePrefix: [ phonePrefix, [
        Validators.required,
        Validators.pattern(/^\+\d+$/)
      ]],
      emailVerificationCode: [null, [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
        Validators.pattern(/^[0-9]+$/)
      ], this.customValidators.validateEmailVerificationCode()],
      phoneVerificationCode: [null, [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
        Validators.pattern(/^[0-9]+$/)
      ], this.customValidators.validatePhoneVerificationCode()],
      firstName: [this.registrationService.getFirstName(), [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern('^.*[AEIOUYaeiouy]+.*$')
      ]],
      lastName: [this.registrationService.getLastName(), [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern('^.*[AEIOUYaeiouy]+.*$')
      ]],
      street: [this.registrationService.getstreet(), Validators.required],
      apartment: this.registrationService.getApartment(),
      postalCode: [this.registrationService.getPostalCode(), [
        Validators.required
      ]],
      acceptTou: [ this.registrationService.getAcceptTOUAndPP(), [
        Validators.requiredTrue
      ]]
    });

    // Add valueChanges subscription for phonePrefix
    this.form.get('phonePrefix').valueChanges.subscribe(() => {
      // Trigger validation of phone field when prefix changes
      this.form.get('phone').updateValueAndValidity();
    });

    this.form.valueChanges.subscribe(val => {
      this.updateData();
    });

  }

  public updateUserAddress(): Observable<any> {

    let addressData = {
      street: this.form.get('street').value,
      apartment: this.form.get('apartment').value,
      city: this.autocomplete?.getPlace()?.address_components?.find(component => component.types?.includes('locality'))?.long_name || null,
      province: this.autocomplete?.getPlace()?.address_components?.find(component => component.types?.includes('administrative_area_level_1'))?.long_name || null,
      postalCode: this.form.get('postalCode').value,
      country: this.countryService.getSelectedCountry().code,
    };

    return from(this.accountControllerService.accountControllerUpdateProfile(addressData))
      .pipe(switchMap((result: any) => {
        return of(result);
      }));
  }


  public createUserAccount(sendConfirmationEmail: boolean = true, sendConfirmationText: boolean = true): Observable<any> {

    let userProfileDTO = {
      firstName: this.form.get('firstName').value,
      lastName: this.form.get('lastName').value,
      street: this.form.get('street').value,
      apartment: this.form.get('apartment').value,
      postalCode: this.form.get('postalCode').value,
      country: this.countryService.getSelectedCountry().code,
      language: this.i18nService.getLanguageIsoCode(),
      phone: this.form.get('phone').value,
      email: this.form.get('email').value,
      acceptTou: this.form.get('acceptTou').value,
      phonePrefix: this.form.get('phonePrefix').value,
      sendConfirmationEmail: sendConfirmationEmail,
      sendConfirmationText: sendConfirmationText
    };

    //ensure that at least phone and email is not null:
    if(userProfileDTO.phone != null &&
      userProfileDTO.phonePrefix != null &&
      userProfileDTO.email != null) {
      return from(this.accountControllerService.accountControllerCreateUserAccount(userProfileDTO))
        .pipe(switchMap((result: any) => {
            this.temporalUserId = result[0];
            if(this.temporalUserId != null) {
              return of(true);
            }
            else {
              return of(false);
            }
        }
      ));
    }
    else {
      return of(false);
    }

  }

  protected updateData() {
    //update registration form
    let saveData = false;
    for(let input in this.form.value) {
      if(this.registrationFields.includes(input)) {
        saveData = true;
        this.registrationService.data[input] = this.form.value[input];
      }
    }

    if(saveData) {
      this.registrationService.saveData();
    }

  }

  public changeAutocompleteCountry(countryCode) {
    if(this.autocomplete != null) {
      this.autocomplete.setComponentRestrictions({'country': countryCode});
    }
  }

  public loadGoogleMapsAutocomplete(): Promise<any> {
    if (this.autocomplete) {
      return Promise.resolve(this.autocomplete);
    }

    const streetElement = document.getElementById('street');
    if (!streetElement) {
      return Promise.reject('Elemento "street" no encontrado');
    }

    return this.googleMapsLoader.load().then(result => {
      const options = {
        componentRestrictions: {country: this.countryService.getSelectedCountry().code}
      };
      this.autocomplete = new google.maps.places.Autocomplete(streetElement, options);

      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace();
        if (place.address_components != null) {
          let streetName: string = null;
          let streetNumber: string = null;
          let postalCode: string = null;
          let street: string = null;
          for (let i = 0; i < place.address_components.length; i++) {
            for (let j = 0; j < place.address_components[i].types.length; j++) {
              if (place.address_components[i].types[j] === "postal_code") {
                postalCode = place.address_components[i].long_name;
              } else if (place.address_components[i].types[j] === "street_number") {
                streetNumber = place.address_components[i].long_name;
              } else if (place.address_components[i].types[j] === "route") {
                streetName = place.address_components[i].long_name;
              }
            }
          }
          if (streetName != null) {
            street = streetName;
            if (streetNumber != null) {
              street += ', ' + streetNumber;
            }
          }
          const values: any = {};
          if (postalCode != null) {
            values['postalCode'] = postalCode;
          }
          if (street != null) {
            values['street'] = street;
          }
          this.form.patchValue(values);

          document.getElementById('apartment').focus();
        }
      });

      return this.autocomplete;
    });
  }

}
